import TextField from 'Containers/Common/TextField';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { authActions } from 'Containers/Auth/store';
import clsx from 'clsx';
import useWindowSize from 'utils/useWindowSize';
import { KeyRound, Mail } from 'lucide-react';
import LogoImage from '../../../../assets/images/logo.png';
import { useEffect, useState } from 'react';
import { ROUTES } from 'routes/constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { toastActions } from 'Containers/Toasts/store';
import LanguageSelector from '../../../Common/LanguageSelector';

const SUPPORT_PHONE_DISPLAY = '+39 353 335 7748';
const SUPPORT_PHONE_HREF = 'tel:+393533357748';

const fieldProps = {
  component: TextField,
  height: '50px',
  margin: '0 0 16px 0',
};

const RecoveryPwdPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const [email, setEmail] = useState('');
  const [resend, setResend] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const validationSchema = {
    email: yup
      .string(t('auth.validation.enterEmail'))
      .email(t('auth.validation.validEmail'))
      .required(t('auth.validation.emailRequired')),
  };

  const sendRecoveryEmail = (value, onSuccess) => {
    if (isSending) return;
    setIsSending(true);
    const safetyTimer = window.setTimeout(() => setIsSending(false), 20000);
    dispatch(
      authActions.SEND_EMAIL_RECOVERY.REQUEST({ email: value }, () => {
        window.clearTimeout(safetyTimer);
        setIsSending(false);
        onSuccess?.();
      }),
    );
  };

  const handleClick = (value) => {
    sendRecoveryEmail(value, () => setEmail(value));
  };

  const handleResend = () => {
    if (resend) return;
    sendRecoveryEmail(email, () => setResend(true));
  };

  const clearExpiredLink = () => {
    if (searchParams.has('token')) searchParams.delete('token');
    if (searchParams.has('name')) searchParams.delete('name');
    setSearchParams(searchParams);
    localStorage.removeItem('tokenRecovery');
    localStorage.removeItem('name');
    dispatch(
      toastActions.OPEN_TOAST.SUCCESS({
        type: 'api',
        props: {
          title: t('auth.recoveryPwdPage.linkExpired'),
          message: t('auth.recoveryPwdPage.sendNewRequest'),
          warning: true,
        },
      }),
    );
  };

  useEffect(() => {
    const token = searchParams.get('token');
    const nomeSalone = searchParams.get('name');
    if (!token) return;

    localStorage.setItem('tokenRecovery', token);
    localStorage.setItem('name', nomeSalone);

    try {
      const expToken = jwtDecode(token);
      if (expToken.exp > Date.now() / 1000) {
        navigate(`${ROUTES.RECOVERY}/password`);
        return;
      }
    } catch (error) {
      // Invalid token: same path as expired
    }

    clearExpiredLink();
  }, []);

  return (
    <div className={clsx(styles.container, { [styles.container_mbl]: isMobile })}>
      <div className={styles.languageSelector}>
        <LanguageSelector />
      </div>
      <div className={styles.card}>
        <img src={LogoImage} alt={t('common.logo')} className={styles.logo} />

        {!email ? (
          <>
            <span className={styles.card_icon} aria-hidden="true">
              <KeyRound size={18} strokeWidth={2.2} />
            </span>
            <h1>{t('auth.recoveryPwdPage.forgotPassword')}</h1>
            <p className={styles.lead}>{t('auth.recoveryPwdPage.description')}</p>
            <Formik
              initialValues={{ email }}
              validationSchema={yup.object().shape(validationSchema)}
              enableReinitialize
              onSubmit={(values) => handleClick(values.email)}
            >
              <Form className={styles.form}>
                <Field
                  {...fieldProps}
                  name="email"
                  id="email"
                  type="email"
                  autoComplete="username"
                  label={t('common.form.email.label')}
                  placeholder={t('auth.recoveryPwdPage.emailPlaceholder')}
                  showIcon
                />
                <button type="submit" className={styles.confirm_button} disabled={isSending}>
                  {isSending
                    ? t('auth.recoveryPwdPage.sending')
                    : t('auth.recoveryPwdPage.sendEmail')}
                </button>
              </Form>
            </Formik>
          </>
        ) : (
          <>
            <span className={styles.card_icon} aria-hidden="true">
              <Mail size={18} strokeWidth={2.2} />
            </span>
            <h1>{t('auth.recoveryPwdPage.emailSent')}</h1>
            <p className={styles.lead}>{t('auth.recoveryPwdPage.checkEmail')}</p>
            <p className={styles.email_value}>{email}</p>
            <p className={styles.hint}>
              {resend ? (
                t('auth.recoveryPwdPage.resent')
              ) : (
                <>
                  {t('auth.recoveryPwdPage.spamCheck')} {t('auth.recoveryPwdPage.or')}{' '}
                  <button
                    type="button"
                    className={styles.text_button}
                    onClick={handleResend}
                    disabled={isSending}
                  >
                    {t('auth.recoveryPwdPage.tryAgain')}
                  </button>
                  .
                </>
              )}
            </p>
            <p className={styles.hint}>
              {t('auth.recoveryPwdPage.needHelp')}{' '}
              <a className={styles.text_button} href={SUPPORT_PHONE_HREF}>
                {SUPPORT_PHONE_DISPLAY}
              </a>
              .
            </p>
          </>
        )}

        <button type="button" className={styles.back_link} onClick={() => navigate(ROUTES.SIGN_IN)}>
          {t('auth.recoveryPwdPage.backToLogin')}
        </button>
      </div>
    </div>
  );
};

export default RecoveryPwdPage;
